import React, { useCallback, useEffect, useMemo, useState } from "react";
import useProductImpressionTracker from "@danishagro/shared/src/gtm/useProductImpressionTracker";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { DA_NewProductSimple } from "@danishagro/shared/src/interfaces/productv2.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useBatchedProductPrices } from "@danishagro/shared/src/hooks/useProductPrices.hook";
import { DA_ProductPrice } from "@danishagro/shared/src/interfaces/price.interface";
import { useFavorites } from "@danishagro/shared/src/contexts/favorites.context";
import { ProductListItemSiloAndHouse } from "@molecules/ProductListItemSiloAndHouse";
import S from "./RelewiseProductList.module.scss";

export interface RelewiseProductListProps {
    products: DA_NewProductSimple[];
    categoryId: string;
    onPricesFetched?: (prices: DA_ProductPrice[]) => void;
}

export const RelewiseProductList = ({
    products,
    categoryId,
    onPricesFetched,
}: RelewiseProductListProps) => {
    const { showPrices, displayFavoritesList } = useAppData();
    const [priceData, setPriceData] = useState<DA_ProductPrice[]>([]);

    const productChunks = useMemo(() => [products], [products]);
    const prices = useBatchedProductPrices(productChunks);

    // Get favorites context
    let favorites;
    // Wrap the useFavorites call in a try-catch block to avoid errors if the provider is not available
    try {
        const favoritesContext = useFavorites();
        favorites = displayFavoritesList ? favoritesContext.favorites : undefined;
    } catch (error) {
        favorites = undefined;
    }

    useEffect(() => {
        if (showPrices && products) {
            setPriceData(prices);
            onPricesFetched?.(prices);
        }
    }, [prices, products, showPrices, onPricesFetched]);

    // Build a priceMap keyed by productNumber for O(1) lookup
    const priceMap = useMemo(() => {
        const map = new Map<string, DA_ProductPrice>();
        priceData.forEach((p) => {
            if (p?.productNumber) {
                map.set(p.productNumber, p);
            }
        });
        return map;
    }, [priceData]);

    // GTM Tracking
    const productRefs = useProductImpressionTracker(products);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "en-GB", { decimals: 2 }),
        []
    );

    return (
        <div className={S.container}>
            {products.map((item, index) => {
                // Transforming the category URL
                const category = item?.url?.split("/").slice(0, -1).join("_").replace(/^_/, "");
                // Determine if the product is a favorite
                const isFavorite = favorites?.has(item.variantEntityId) ?? false;

                return (
                    <ProductListItemSiloAndHouse
                        ref={(el) => (productRefs.current[index] = el)}
                        categoryId={categoryId}
                        data-id={item?.masterProductNumber}
                        data-name={item?.displayName}
                        data-category={category}
                        eagerLoading={index < 8}
                        data-listid={item?.entityId} // TODO: figure out listId
                        data-price={formatPrice(item?.price?.priceWithoutVat)}
                        key={`${item?.entityId}--${index}`}
                        {...item}
                        price={showPrices ? priceMap.get(item.productNumber) : undefined}
                        hasDiscount={
                            priceMap.get(item.productNumber)?.hasDiscount ?? item.hasDiscount
                        }
                        isFavorite={isFavorite}
                        version="v1"
                        fetchPriority={index < 8}
                    />
                );
            })}
        </div>
    );
};
